import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from 'react-helmet-async';
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";

const HomePage = lazy(() => import("./pages/HomePage").then(m => ({ default: m.HomePage })));
const RealEstatePage = lazy(() => import("./pages/RealEstatePage").then(m => ({ default: m.RealEstatePage })));
const EditorialPage = lazy(() => import("./pages/EditorialPage").then(m => ({ default: m.EditorialPage })));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage").then(m => ({ default: m.PortfolioPage })));
const AboutPage = lazy(() => import("./pages/AboutPage").then(m => ({ default: m.AboutPage })));
const AdminPage = lazy(() => import("./pages/AdminPage").then(m => ({ default: m.AdminPage })));
const BlogPage = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage").then(m => ({ default: m.BlogPostPage })));
const ProjectPostPage = lazy(() => import("./pages/ProjectPostPage").then(m => ({ default: m.ProjectPostPage })));
const FineArtPrintsPage = lazy(() => import("./pages/FineArtPrintsPage").then(m => ({ default: m.FineArtPrintsPage })));
const LocationPage = lazy(() => import("./pages/LocationPage"));

const SEO_LOCATIONS = [
  { path: "/real-estate-photographer-gold-coast", location: "Gold Coast", region: "Northern Rivers", title: "Real Estate Photographer Gold Coast | Balduzzi Living", desc: "Professional real estate photography on the Gold Coast & Northern Rivers. Fast turnaround, drone, film, and editorial property photography. Trusted by leading agents." },
  { path: "/real-estate-photographer-brisbane", location: "Brisbane", region: "Southeast Queensland", title: "Real Estate Photographer Brisbane | Balduzzi Living", desc: "Professional real estate photography in Brisbane & Southeast Queensland. MLS-ready images, drone aerials, cinematic film. 24-48 hour delivery guaranteed." },
  { path: "/real-estate-photographer-sunshine-coast", location: "Sunshine Coast", region: "Noosa", title: "Real Estate Photographer Sunshine Coast | Balduzzi Living", desc: "Professional real estate photography on the Sunshine Coast & Noosa. Editorial-quality property images, drone, film, and fast turnaround for agents." },
  { path: "/real-estate-photographer-yamba", location: "Yamba", region: "Clarence Valley", title: "Real Estate Photographer Yamba | Balduzzi Living", desc: "Professional real estate photography in Yamba & Clarence Valley. Clean, bright property images with drone aerials and cinematic film. Fully insured." },
  { path: "/real-estate-photographer-ballina", location: "Ballina", region: "Northern Rivers", title: "Real Estate Photographer Ballina | Balduzzi Living", desc: "Professional real estate photography in Ballina & Northern Rivers. Reliable property photography with 24-48 hour delivery. Registered drone operator." },
  { path: "/real-estate-photographer-lismore", location: "Lismore", region: "Northern Rivers", title: "Real Estate Photographer Lismore | Balduzzi Living", desc: "Professional real estate photography in Lismore & Northern Rivers. Consistent, high-quality property images for agents and developers. Fully insured." },
  { path: "/real-estate-photographer-alstonville", location: "Alstonville", region: "Ballina Shire", title: "Real Estate Photographer Alstonville | Balduzzi Living", desc: "Professional real estate photography in Alstonville & Ballina Shire. Fast turnaround, professional equipment, and editorial-quality property images." },
  { path: "/real-estate-photographer-mullumbimby", location: "Mullumbimby", region: "Byron Shire", title: "Real Estate Photographer Mullumbimby | Balduzzi Living", desc: "Professional real estate photography in Mullumbimby & Byron Shire. Property photography, drone aerials, and cinematic film for agents and developers." },
  { path: "/real-estate-photographer-bangalow", location: "Bangalow", region: "Byron Hinterland", title: "Real Estate Photographer Bangalow | Balduzzi Living", desc: "Professional real estate photography in Bangalow & Byron Hinterland. Editorial-quality property images with drone, film, and fast delivery." },
  { path: "/real-estate-photographer-tweed-heads", location: "Tweed Heads", region: "Tweed Coast", title: "Real Estate Photographer Tweed Heads | Balduzzi Living", desc: "Professional real estate photography in Tweed Heads & Tweed Coast. Clean, bright property images for agents. Drone, film, and 24-48hr delivery." },
];

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const PageLoader = () => (
  <div className="min-h-screen bg-cream flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-charcoal/20 border-t-charcoal rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App min-h-screen bg-cream flex flex-col">
          <Navigation />
          <div className="flex-1">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/real-estate" element={<RealEstatePage />} />
                <Route path="/editorial" element={<EditorialPage />} />
                <Route path="/portfolio" element={<PortfolioPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/projects/:slug" element={<ProjectPostPage />} />
                <Route path="/fine-art-prints" element={<FineArtPrintsPage />} />
                {SEO_LOCATIONS.map(loc => (
                  <Route key={loc.path} path={loc.path} element={
                    <LocationPage location={loc.location} region={loc.region} metaTitle={loc.title} metaDescription={loc.desc} />
                  } />
                ))}
              </Routes>
            </Suspense>
          </div>
          <Footer />
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#2C2C2C',
                color: '#F5F1E8',
                border: 'none',
              },
            }}
          />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
